// XScene: the character's place. Fills its positioned parent (position: absolute; inset: 0).
// Plays the working-together loop for stages that have one (loopFor(ch, stage) in characters.ts), otherwise shows the
// stage still with a slow drift. Modes add the prototype's veils: live · dim · night · blur · spot · clear.
import type { JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import type { CharId } from '@shared/types';
import { C, loopFor } from '@shared/content/characters';
import { useSize } from '../lib/useSize';
import { useReducedMotion } from '../layout';

export type SceneMode = 'live' | 'dim' | 'night' | 'blur' | 'spot' | 'clear';

export interface XSceneProps {
  ch: CharId;
  /** Place stage 1-7 (state.buddy.stage). Default 1. */
  stage?: number;
  mode?: SceneMode;
  /** object-position of the picture (default '50% 50%'). */
  pos?: string;
  /** Play the loop video when this stage has one (default true). False shows the loop's poster instead. */
  video?: boolean;
  /** Slow drift on stills (default true). Off in blur mode and with reduced motion. */
  drift?: boolean;
  /** Alt text; default "{name} {placeAt}". Pass '' when decorative. */
  alt?: string;
  /** Fixed size in px (thumbnails, e.g. U1's 120x213 place card). Without w/h the scene fills its positioned parent. */
  w?: number;
  h?: number;
  class?: string;
  style?: JSX.CSSProperties;
}

const TOP = 'linear-gradient(180deg, rgba(6,5,4,0.62) 0%, rgba(6,5,4,0) 100%)';

export function XScene({ ch, stage = 1, mode = 'live', pos = '50% 50%', video = true, drift = true, alt, w: fw, h: fh, class: cls, style }: XSceneProps) {
  const root = useRef<HTMLDivElement>(null);
  const { w } = useSize(root, { w: fw || 390, h: fh || 844 });
  const reduced = useReducedMotion();
  const st = Math.max(1, Math.min(7, Math.round(stage) || 1));
  const def = C[ch];
  const loop = loopFor(ch, st);
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [ch, st]);
  const useVideo = !!loop && video && !reduced && !failed;
  const blur = mode === 'blur';
  const pad = blur ? 40 : 0;
  const still = loop ? loop.poster : def.assets.stage(st);
  const doDrift = !useVideo && !blur && drift && !reduced;
  const label = alt != null ? alt : `${def.name} ${def.placeAt}`;

  const media: JSX.CSSProperties = {
    position: 'absolute', left: -pad, top: -pad, width: `calc(100% + ${pad * 2}px)`, height: `calc(100% + ${pad * 2}px)`,
    maxWidth: 'none', objectFit: 'cover', objectPosition: pos, filter: blur ? 'blur(18px) brightness(0.36)' : 'none',
  };
  const veil = {
    live: { top: TOP, veil: 'transparent', low: 'linear-gradient(0deg, rgba(6,5,4,0.92) 0%, rgba(6,5,4,0.55) 45%, rgba(6,5,4,0) 100%)', lowH: '56%' },
    dim: { top: TOP, veil: 'rgba(6,5,4,0.5)', low: 'linear-gradient(0deg, rgba(6,5,4,0.92) 0%, rgba(6,5,4,0) 100%)', lowH: '50%' },
    night: { top: TOP, veil: 'rgba(5,4,8,0.72)', low: 'linear-gradient(0deg, rgba(5,4,8,0.9) 0%, rgba(5,4,8,0) 100%)', lowH: '40%' },
    blur: { top: 'transparent', veil: 'rgba(6,5,4,0.25)', low: 'transparent', lowH: '0' },
    spot: { top: 'transparent', veil: `radial-gradient(circle at 50% 70%, rgba(6,5,4,0) 0px, rgba(6,5,4,0.25) ${Math.round(w * 0.26)}px, rgba(6,5,4,0.93) ${Math.round(w * 0.66)}px)`, low: 'transparent', lowH: '0' },
    clear: { top: 'transparent', veil: 'transparent', low: 'transparent', lowH: '0' },
  }[mode];

  return (
    <div ref={root} class={cls} style={{ ...(fw || fh ? { position: 'relative', width: fw, height: fh, flex: 'none' } : { position: 'absolute', inset: 0 }), overflow: 'hidden', background: def.bg, ...style }}>
      <img
        src={still} alt={useVideo ? '' : label} draggable={false} decoding="async"
        style={{ ...media, animation: doDrift ? 'xmDrift 28s ease-in-out infinite alternate' : 'none' }}
      />
      {useVideo && loop && <LoopVideo src={loop.src} poster={loop.poster} label={label} style={media} onFail={() => setFailed(true)} />}
      {veil.top !== 'transparent' && <div aria-hidden="true" style={{ position: 'absolute', left: 0, right: 0, top: 0, height: 160, background: veil.top }} />}
      {veil.veil !== 'transparent' && <div aria-hidden="true" style={{ position: 'absolute', inset: 0, background: veil.veil }} />}
      {veil.low !== 'transparent' && <div aria-hidden="true" style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: veil.lowH, background: veil.low }} />}
    </div>
  );
}

function LoopVideo({ src, poster, label, style, onFail }: { src: string; poster: string; label: string; style: JSX.CSSProperties; onFail: () => void }) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const v = ref.current;
    if (!v) return undefined;
    // Autoplay rules check the muted *attribute* on some WebViews; set both, then play.
    v.muted = true; v.defaultMuted = true; v.setAttribute('muted', ''); v.setAttribute('playsinline', ''); v.setAttribute('webkit-playsinline', '');
    const play = () => { const p = v.play(); if (p) p.catch(() => { /* autoplay blocked: the poster stays */ }); };
    play();
    const vis = () => { if (document.visibilityState === 'visible') play(); };
    document.addEventListener('visibilitychange', vis);
    return () => document.removeEventListener('visibilitychange', vis);
  }, [src]);
  return (
    <video
      ref={ref} src={src} poster={poster} autoplay muted loop playsInline preload="auto" aria-label={label}
      disablePictureInPicture disableRemotePlayback
      onError={onFail}
      style={{ ...style, background: 'transparent' }}
    />
  );
}
